/**
 * Anchor Links component is used to drive users to specific areas of content-heavy pages
 *
 * @module views/components/AnchorLinks
 * @memberof -Common
*/
import './AnchorLinks.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Grid from '@ulta/core/components/GridContainer/GridContainer';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import AnchorLink from '@ulta/components/AnchorLink/AnchorLink';

import { useStickToHeader } from '@ulta/hooks/useStickToHeader/useStickToHeader';

import * as utils from './AnchorLinks';

/**
 * Represents a AnchorLinks component
 *
 * @method
 * @param { AnchorLinksProps} props - React properties passed from composition
 * @returns AnchorLinks
*/
export const AnchorLinks = function( props ){
  const { icon, isSticky, anchors } = props;
  // Ref for the anchor div
  const offsetElementRef = useRef( null );
  // Ref for an element we place before the anchor for tracking start position
  const positionElementRef = useRef();
  // Custom Hook call for sticky position interaction w/ header's hide/show animation
  const [shouldOffsetElement, headerOffset] = useStickToHeader( { positionElementRef } );
  const hasImages = utils.checkForImages( { anchors } );

  if( !anchors ){
    return null;
  }

  const style = ( shouldOffsetElement && isSticky ) ? { transform: `translateY(${headerOffset}px)` } : undefined;

  return (
    <>
      <div className='AnchorLinks_positionElementRef'
        ref={ positionElementRef }
      />
      <div className={
        classNames( 'AnchorLinks', {
          'AnchorLinks--noImage': !hasImages,
          'AnchorLinks--sticky': isSticky
        } )
      }
      ref={ offsetElementRef }
      { ...spreadProps( { style } ) }
      >
        {
          <Grid>
            <div className='AnchorLinks__itemsWrapper'>
              <div className='AnchorLinks__items'>
                { anchors?.map( ( anchor, index )=>(
                  <AnchorLink
                    image={ utils.checkImagesAndSticky( { hasImages, isSticky, image: anchor.image } ) }
                    action={ anchor.action }
                    key={ index }
                    icon={ icon }
                  >
                  </AnchorLink>
                ) )
                }
              </div>
            </div>
          </Grid>
        }
      </div>
    </>
  );
};

/**
 * Checks for images
 * @param {Array} anchors
 * @returns {bool} with/without image
*/
export const checkForImages = ( data ) => {
  const { anchors } = data || {};
  for ( let i = 0; i < anchors?.length; i++ ){
    if( !anchors[i].image?.imageUrl ){
      return false;
    }
  }
  return true;
};

/**
 * Checks whether to send image content or null depending on hasImage flag
 * @param {bool} hasImages
 * @param {bool} isSticky
 * @param {Object} image
 * @returns {Object} image or null
*/
export const checkImagesAndSticky = ( data ) => {
  const { hasImages, isSticky, image } = data || {};
  if( hasImages && !isSticky ){
    return image;
  }
  return null;
};

/**
 * Property type definitions
 * @typedef AnchorLinksProps
 * @type {object}
 * @property {Array} anchors - List of anchors
 * @property {string} icon - Arrow down icon
 * @property {bool} isSticky - Flag for sticky anchor links
 */
export const propTypes =  {
  anchors: PropTypes.array.isRequired,
  icon: PropTypes.string,
  isSticky: PropTypes.bool
};

AnchorLinks.propTypes = propTypes;

export default AnchorLinks;
